// mds-dojo — boot-time widget/module registration helper. Extracted from the app
// entry (Typescript/main.ts) so every consuming app's `import.meta.glob` boot is a
// couple of lines instead of a hand-rolled loop. The glob + the path→id mapping stay
// app-side (they depend on the app's own directory layout), but the loop that feeds
// the registries is identical everywhere, so it lives here.

import parser from './parser';
import { __register } from './require';

export interface RegisterModulesOptions {
  /**
   * Map a module's glob path to its dojo id (e.g. `/src/widgets/Foo/Bar.ts` →
   * `Foo/Bar`), or `null` to skip it. App-specific — it encodes the app's directory
   * layout — so the app supplies it.
   */
  pathToId: (path: string) => string | null;
  /**
   * Ids to never register (the entry / bootstrap modules, which are not widgets).
   * @default ['main', 'bootstrap', 'widgets-root']
   */
  skipIds?: Iterable<string>;
}

const DEFAULT_SKIP_IDS = ['main', 'bootstrap', 'widgets-root'];

/**
 * **mds-dojo** — register a bag of eagerly-globbed modules with the two runtime
 * registries: every module by id for synchronous {@link mdsRequire} lookups, and
 * each module's default-export constructor with the parser so a `data-dojo-type`
 * resolves. NOT a vanilla Dojo 1.x export — it replaces the AMD loader's
 * "make every widget resolvable" job.
 *
 * Typical boot (in the app entry):
 * ```ts
 * import { registerModules } from 'dojo/registerModules';
 *
 * const modules = import.meta.glob(['/src/widgets/** /*.{ts,js}'], { eager: true });
 * registerModules(modules, {
 *   pathToId: (p) => p.match(/^\/src\/widgets\/(.+)\.(?:ts|js)$/)?.[1] ?? null,
 * });
 * ```
 *
 * @param modules the eager glob result: `{ [path]: moduleNamespace }`.
 * @param options {@link RegisterModulesOptions.pathToId} (required) and an optional
 *   {@link RegisterModulesOptions.skipIds} set.
 * @returns the ids that were registered (handy for diagnostics / tests).
 */
export function registerModules(
  modules: Record<string, any>,
  options: RegisterModulesOptions,
): string[] {
  const skip = new Set<string>(options.skipIds ?? DEFAULT_SKIP_IDS);
  const registered: string[] = [];
  for (const [path, mod] of Object.entries(modules)) {
    const id = options.pathToId(path);
    if (!id || skip.has(id)) { continue; }
    // Register the whole module for synchronous mdsRequire("id") lookups.
    __register(id, mod);
    const ctor = mod && mod.default;
    if (typeof ctor === 'function') {
      parser.register(id, ctor);
    }
    registered.push(id);
  }
  return registered;
}

export default registerModules;
